import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';

function authHeaders() {
    const token = localStorage.getItem('Token');
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function pickValue(obj, ...keys) {
    if (!obj || typeof obj !== 'object') return undefined;
    for (const key of keys) {
        const val = obj[key];
        if (val !== undefined && val !== null && val !== '') return val;
    }
    return undefined;
}

const VALID_QUOTE_STATUSES = new Set(['REQUESTED', 'UNDER_REVIEW', 'CONFIRMED', 'REJECTED']);
export const TERMINAL_QUOTE_STATUSES = new Set(['CONFIRMED', 'REJECTED']);

export function normalizeQuoteStatusKey(quoteStatus) {
    return String(quoteStatus ?? '')
        .trim()
        .toUpperCase()
        .replace(/[\s_-]+/g, '');
}

export function isTerminalQuoteStatus(quoteStatus) {
    return TERMINAL_QUOTE_STATUSES.has(normalizeQuoteStatusKey(quoteStatus));
}

function resolveQuoteStatus(raw) {
    const picked = pickValue(raw, 'quoteStatus', 'Quote_Status');
    let status = String(picked ?? 'REQUESTED').trim().toUpperCase().replace(/\s+/g, '_');
    if (!status || !VALID_QUOTE_STATUSES.has(status)) {
        return 'REQUESTED';
    }
    return status;
}

export function normalizeCateringQuoteLineItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const lineId = pickValue(raw, 'cateringQuoteLineItemId', 'Catering_Quote_LineItem_Id');
    const itemId = pickValue(raw, 'cateringItemId', 'Catering_Item_Id');
    if (lineId == null && itemId == null) return null;
    return {
        cateringQuoteLineItemId: lineId != null ? Number(lineId) : null,
        cateringItemId: itemId != null ? Number(itemId) : null,
        foodCategoryId: pickValue(raw, 'foodCategoryId', 'Food_Category_Id'),
        itemName: pickValue(raw, 'itemName', 'Item_Name') ?? '',
        meal: pickValue(raw, 'meal', 'Meal') ?? '',
        quantity: Number(pickValue(raw, 'quantity', 'Quantity') ?? 0),
        unitPrice: pickValue(raw, 'unitPrice', 'Unit_Price'),
        price: pickValue(raw, 'price', 'Price'),
    };
}

export function normalizeCateringQuote(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const id = pickValue(raw, 'cateringQuoteId', 'Catering_Quote_Id');
    if (id == null) return null;
    const quote = {
        cateringQuoteId: Number(id),
        customerId: pickValue(raw, 'customerId', 'Customer_Id'),
        customerName: pickValue(raw, 'customerName', 'Customer_Name') ?? '',
        customerPhone: pickValue(raw, 'customerPhone', 'Customer_Phone') ?? '',
        restaurantId: pickValue(raw, 'restaurantId', 'Restaurant_Id'),
        cateringTempId: pickValue(raw, 'cateringTempId', 'Catering_Temp_Id'),
        packageName: pickValue(raw, 'packageName', 'Package_Name') ?? '',
        pricePerPerson: pickValue(raw, 'pricePerPerson', 'Price_Per_Person'),
        numberOfGuests: pickValue(raw, 'numberOfGuests', 'Number_Of_Guests'),
        eventDate: pickValue(raw, 'eventDate', 'Event_Date') ?? '',
        eventTime: pickValue(raw, 'eventTime', 'Event_Time') ?? '',
        eventType: pickValue(raw, 'eventType', 'Event_Type') ?? '',
        deliveryType: pickValue(raw, 'deliveryType', 'Delivery_Type') ?? '',
        deliveryAddress: pickValue(raw, 'deliveryAddress', 'Delivery_Address') ?? '',
        platformFee: pickValue(raw, 'platformFee', 'Platform_Fee'),
        gst: pickValue(raw, 'gst', 'GST'),
        deliveryFee: pickValue(raw, 'deliveryFee', 'Delivery_Fee'),
        convienceFee: pickValue(raw, 'convienceFee', 'Convience_Fee'),
        estimatedTotal: pickValue(raw, 'estimatedTotal', 'Estimated_Total'),
        quotedTotal: pickValue(raw, 'quotedTotal', 'Quoted_Total'),
        quoteStatus: resolveQuoteStatus(raw),
        createdBy: pickValue(raw, 'createdBy', 'Created_By'),
        createdDate: pickValue(raw, 'createdDate', 'created_date', 'Created_Date') ?? '',
        updatedBy: pickValue(raw, 'updatedBy', 'Updated_By'),
        updatedDate: pickValue(raw, 'updatedDate', 'updated_date', 'Updated_Date') ?? '',
        lineItems: [],
    };
    const items = raw.lineItems ?? raw.LineItems ?? [];
    if (Array.isArray(items)) {
        quote.lineItems = items.map(normalizeCateringQuoteLineItem).filter(Boolean);
    }
    return quote;
}

export const CATERING_QUOTES_LIST_PATH =
    '/home?tab=orders&orderType=catering&cateringView=quotes';

export async function fetchCateringQuotesByRestaurant(restaurantId, { quoteStatus } = {}) {
    const params = {};
    if (quoteStatus) params.quoteStatus = quoteStatus;
    const res = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.cateringQuotesByRestaurantId(restaurantId),
        { params, headers: authHeaders() }
    );
    const rows = res.data?.quotes ?? res.data?.Quotes ?? [];
    return Array.isArray(rows) ? rows.map(normalizeCateringQuote).filter(Boolean) : [];
}

export async function fetchCateringQuoteById(quoteId) {
    const res = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.cateringQuoteById(quoteId),
        { headers: authHeaders() }
    );
    return normalizeCateringQuote(res.data?.quote ?? res.data);
}

export async function updateCateringQuote(quoteId, payload) {
    const res = await axiosRestaurantInstance.put(
        RESTAURANT_API_PATHS.cateringQuoteById(quoteId),
        payload,
        { headers: authHeaders() }
    );
    return normalizeCateringQuote(res.data?.quote ?? res.data);
}

export async function createCateringQuote(payload) {
    const res = await axiosRestaurantInstance.post(
        RESTAURANT_API_PATHS.cateringQuotesCreate,
        payload,
        { headers: authHeaders() }
    );
    const quote = normalizeCateringQuote(res.data?.quote ?? res.data);
    return {
        cateringQuoteId: res.data?.cateringQuoteId ?? quote?.cateringQuoteId,
        quote,
    };
}
